import React, { useState } from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const labelClasses = "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block";
const errorClasses = "text-sm font-medium text-destructive mt-1";
function ContactForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
          <CircleCheck className="h-12 w-12 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold">Request Sent!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Request
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4 py-4">
      {}
      <div>
        <label htmlFor="full_name" className={labelClasses}>
          Full Name <span className="text-destructive">*</span>
        </label>
        <Controller control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field,
        fieldState: {
          error
        }
      }) => <>
              <input {...field} id="full_name" type="text" placeholder="Nguyen Van A" className={inputClasses} aria-invalid={!!error} />
              {error && <p className={errorClasses}>{error.message}</p>}
            </>} />
      </div>

      {}
      <div>
        <label htmlFor="company_name" className={labelClasses}>
          Company / Organization <span className="text-destructive">*</span>
        </label>
        <Controller control={control} name="company_name" rules={{
        required: "Company name is required"
      }} render={({
        field,
        fieldState: {
          error
        }
      }) => <>
              <input {...field} id="company_name" type="text" placeholder="NUCETECH Partner Ltd." className={inputClasses} aria-invalid={!!error} />
              {error && <p className={errorClasses}>{error.message}</p>}
            </>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {}
        <div>
          <label htmlFor="email" className={labelClasses}>
            Email Address <span className="text-destructive">*</span>
          </label>
          <Controller control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field,
          fieldState: {
            error
          }
        }) => <>
                <input {...field} id="email" type="email" placeholder="example@domain.com" className={inputClasses} aria-invalid={!!error} />
                {error && <p className={errorClasses}>{error.message}</p>}
              </>} />
        </div>

        {}
        <div>
          <label htmlFor="phone" className={labelClasses}>
            Phone Number <span className="text-destructive">*</span>
          </label>
          <Controller control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
            message: "Invalid phone number"
          }
        }} render={({
          field,
          fieldState: {
            error
          }
        }) => <>
                <input {...field} id="phone" type="tel" placeholder="+84 90 123 4567" className={inputClasses} aria-invalid={!!error} />
                {error && <p className={errorClasses}>{error.message}</p>}
              </>} />
        </div>
      </div>

      {}
      <div>
        <label htmlFor="preferred_contact_time" className={labelClasses}>
          Preferred Contact Time
        </label>
        <Controller control={control} name="preferred_contact_time" render={({
        field
      }) => <div className="relative">
              <select {...field} id="preferred_contact_time" className={inputClasses}>
                <option value="No Preference">No Preference</option>
                <option value="Morning (8:00 - 12:00)">Morning (8:00 - 12:00)</option>
                <option value="Afternoon (12:00 - 16:00)">Afternoon (12:00 - 16:00)</option>
                <option value="Evening (16:00 - 20:00)">Evening (16:00 - 20:00)</option>
              </select>
            </div>} />
      </div>

      {}
      <div>
        <label htmlFor="project_description" className={labelClasses}>
          Project / Consultation Details <span className="text-destructive">*</span>
        </label>
        <Controller control={control} name="project_description" rules={{
        required: "Please describe your project",
        minLength: {
          value: 20,
          message: "Description must be at least 20 characters"
        },
        maxLength: {
          value: 2000,
          message: "Description must not exceed 2000 characters"
        }
      }} render={({
        field,
        fieldState: {
          error
        }
      }) => <>
              <textarea {...field} id="project_description" placeholder="Describe the project, challenges, and what kind of advice you need..." className={`${inputClasses} min-h-[120px] resize-y`} aria-invalid={!!error} />
              {error && <p className={errorClasses}>{error.message}</p>}
            </>} />
      </div>

      {}
      {submitError && <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive flex items-center gap-2">
          <CircleAlert className="h-4 w-4" />
          <span>{submitError}</span>
        </div>}

      {}
      <Button type="submit" className="w-full bg-[hsl(199,89%,48%)] hover:bg-[hsl(199,89%,38%)] text-white font-semibold h-11" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Submitting Request...
          </> : "Submit Consultation Request"}
      </Button>
    </form>;
}
export default function YeuCauTuVan() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="yêu-cầu-tư-vấn" className="dark bg-background text-foreground relative w-full overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/construction_blueprint_overlay.png')] bg-cover bg-center opacity-40 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      <div className="container relative z-10 mx-auto px-4 text-center">
        <div ref={ref} className={`mx-auto max-w-4xl transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="mb-6 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
            Ready to Transform Your Project?
          </h2>
          <p className="mb-10 text-lg text-slate-300 sm:text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed">
            Leverage our advanced research and digital transformation solutions for your
            construction challenges.
          </p>

          <Dialog>
            <DialogTrigger asChild>
              <Button size="lg" className="h-14 px-8 text-lg font-bold bg-[hsl(199,89%,48%)] hover:bg-[hsl(199,89%,38%)] text-white shadow-[0_0_20px_rgba(14,165,233,0.3)] hover:shadow-[0_0_30px_rgba(14,165,233,0.5)] transition-all duration-300 border-none">
                Get a Consultation
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-card border-border">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-foreground">
                  Consultation Request Form
                </DialogTitle>
                <DialogDescription>
                  Fill out the details below and our experts will get back to you shortly.
                </DialogDescription>
              </DialogHeader>

              <FormProvider formId="consultation_form_solutions" sectionName="consultation-cta" defaultValues={{
              full_name: "",
              company_name: "",
              email: "",
              phone: "",
              project_description: "",
              preferred_contact_time: "No Preference"
            }} validationRules={{
              full_name: {
                required: true
              },
              company_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              project_description: {
                required: true,
                minLength: 20,
                maxLength: 2000
              }
            }} submitText="Submit Consultation Request" successMessage="Thank you! Your consultation request has been received. We will contact you soon." errorMessage="Failed to submit request. Please try again.">
                <ContactForm />
              </FormProvider>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>;
}